import { motion } from 'motion/react';
import { LayoutDashboard, Users, Mail, Palette, BarChart3, Settings, MessageSquare, LifeBuoy, LogOut, Bell, HardDrive, Sparkles } from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '@/src/infrastructure/lib/utils';
import { BrandLogo } from '@/src/modules/components/BrandLogo';
import { useAuth } from '@/src/modules/auth/useAuth';
import { avatarSrc } from '@/src/infrastructure/lib/avatar';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Users, label: 'Users', path: '/users' },
  { icon: Mail, label: 'Invitations', path: '/invitations' },
  { icon: Palette, label: 'Templates', path: '/templates' },
  { icon: Sparkles, label: 'Type B Studio', path: '/type-b' },
  { icon: HardDrive, label: 'Media', path: '/media' },
  { icon: MessageSquare, label: 'Feedback', path: '/feedback' },
  { icon: LifeBuoy, label: 'Support', path: '/support' },
  { icon: Bell, label: 'Notifications', path: '/notifications' },
  { icon: BarChart3, label: 'Analytics', path: '/analytics' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

const sidebarVariants = {
  open: { width: '18rem' },
  closed: { width: '4.5rem' },
};

const labelVariants = {
  open: { opacity: 1, x: 0, transition: { duration: 0.2, ease: 'easeOut' as const } },
  closed: { opacity: 0, x: -10, transition: { duration: 0.15, ease: 'easeIn' as const } },
};

const transitionProps = {
  type: 'tween' as const,
  ease: 'easeOut' as const,
  duration: 0.25,
};

interface SidebarProps {
  isCollapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

export function Sidebar({ isCollapsed, onCollapsedChange }: SidebarProps) {
  const { user, profile, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const onSignOut = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };
  // Source of truth is the local DB profile — Firebase's photoURL/displayName
  // are baked into the ID token and only refresh on re-auth, so reading them
  // here would make profile edits look stale until next sign-in.
  const displayName =
    profile?.name || user?.displayName || user?.email?.split('@')[0] || 'Admin';
  const initial = displayName.charAt(0).toUpperCase();
  const photoUrl = avatarSrc(profile?.avatar ?? user?.photoURL ?? null, profile?.updatedAt);
  const roleLabel = isAdmin ? 'System Admin' : 'User';

  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        className="fixed left-0 top-0 h-screen bg-surface-container-lowest/80 backdrop-blur-xl border-r border-border-subtle hidden md:flex flex-col py-8 px-3 z-50 overflow-hidden"
        initial={false}
        animate={isCollapsed ? 'closed' : 'open'}
        variants={sidebarVariants}
        transition={transitionProps}
        onMouseEnter={() => onCollapsedChange(false)}
        onMouseLeave={() => onCollapsedChange(true)}
      >
        <div className={cn(
          'flex items-center justify-center border-b border-border-subtle',
          isCollapsed ? 'h-16 px-2 py-2' : 'px-3 py-6 pb-8'
        )}>
          <div className={cn(
            'flex items-center justify-center rounded-lg',
            isCollapsed ? 'w-9 h-9' : 'w-full'
          )}>
            <BrandLogo
              variant={isCollapsed ? "icon" : "full"}
              size={isCollapsed ? "sm" : "lg"}
              href="/dashboard"
              className={cn(
                isCollapsed ? 'h-9 w-9' : 'h-16 w-auto max-w-[140px]'
              )}
            />
          </div>
        </div>

        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              title={isCollapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group whitespace-nowrap',
                  isActive
                    ? 'text-primary font-bold bg-primary/10 border-l-2 border-primary'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-white/5'
                )
              }
            >
              <item.icon className={cn('w-5 h-5 shrink-0 transition-colors', 'group-hover:text-primary')} />
              <motion.span
                variants={labelVariants}
                animate={isCollapsed ? 'closed' : 'open'}
                className="font-sans overflow-hidden"
              >
                {item.label}
              </motion.span>
            </NavLink>
          ))}
        </nav>

        <div
          className={cn(
            'mt-auto flex items-center gap-3 rounded-2xl transition-all duration-200',
            isCollapsed ? 'px-1' : 'px-2 py-3 glass-panel'
          )}
        >
          <div className="w-10 h-10 shrink-0 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold overflow-hidden border border-primary/20">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <span>{initial}</span>
            )}
          </div>
          <motion.div
            variants={labelVariants}
            animate={isCollapsed ? 'closed' : 'open'}
            className="flex-1 overflow-hidden"
          >
            <p className="text-sm font-bold text-on-surface truncate">{displayName}</p>
            <p className="text-[10px] text-on-surface-variant uppercase truncate">{roleLabel}</p>
          </motion.div>
          <button
            type="button"
            onClick={onSignOut}
            title="Sign out"
            aria-label="Sign out"
            className={cn(
              'p-2 rounded-lg text-on-surface-variant hover:text-rose-300 hover:bg-rose-500/10 transition-colors',
              isCollapsed && 'hidden',
            )}
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </motion.aside>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 w-full z-50 md:hidden bg-surface-container-lowest/80 backdrop-blur-lg border-t border-border-subtle flex justify-around items-center h-16 px-4">
        {navItems.slice(0, 4).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center transition-all duration-200',
                isActive ? 'text-primary' : 'text-on-surface-variant'
              )
            }
          >
            <item.icon className="w-6 h-6" />
            <span className="text-[10px] uppercase font-bold tracking-tight mt-1">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}
